import { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useAuth } from '../../contexts/AuthContext';
import {
  Loader2, Brain, Plus, Check, X, AlertTriangle, Lightbulb,
  HandshakeIcon, CircleSlash, Zap, ChevronDown, ChevronUp,
  Pencil, Archive, Trash2, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_CONFIG = {
  BLOCAGE: { label: 'Blocage', color: 'bg-red-500/10 text-red-700 border-red-200', icon: CircleSlash, dotColor: 'bg-red-500' },
  PROMESSE: { label: 'Promesse', color: 'bg-amber-500/10 text-amber-700 border-amber-200', icon: HandshakeIcon, dotColor: 'bg-amber-500' },
  FAIT_IMPORTANT: { label: 'Fait important', color: 'bg-blue-500/10 text-blue-700 border-blue-200', icon: Lightbulb, dotColor: 'bg-blue-500' },
  DECISION: { label: 'Decision', color: 'bg-emerald-500/10 text-emerald-700 border-emerald-200', icon: Check, dotColor: 'bg-emerald-500' },
};

const STATUS_CONFIG = {
  active: { label: 'Actif', color: 'bg-emerald-500/10 text-emerald-700' },
  resolved: { label: 'Resolu', color: 'bg-zinc-500/10 text-zinc-500' },
  dismissed: { label: 'Ignore', color: 'bg-zinc-500/10 text-zinc-400' },
};

export function KnowledgeDialog({ open, onOpenChange, clone }) {
  const { token, user } = useAuth();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('active');
  const [addOpen, setAddOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [newItem, setNewItem] = useState({ type: 'FAIT_IMPORTANT', content: '', context: '' });

  const canManage = user?.role === 'owner' || user?.role === 'admin' || clone?.linked_user_id === user?.id;
  const isAdmin = user?.role === 'owner' || user?.role === 'admin';

  const fetchData = useCallback(async () => {
    if (!clone?.id || !open) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '200' });
      if (filterType !== 'all') params.set('type', filterType);
      if (filterStatus !== 'all') params.set('status', filterStatus);

      const [itemsRes, statsRes] = await Promise.all([
        axios.get(`${API}/persons/${clone.id}/knowledge?${params}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/persons/${clone.id}/knowledge/stats`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setItems(itemsRes.data.items || []);
      setStats(statsRes.data);
    } catch (e) {
      toast.error('Erreur chargement knowledge');
    } finally {
      setLoading(false);
    }
  }, [clone?.id, open, token, filterType, filterStatus]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleExtract = async () => {
    setExtracting(true);
    try {
      const res = await axios.post(`${API}/persons/${clone.id}/knowledge/extract`, { dry_run: false }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = res.data;
      if (d.status === 'no_memories') {
        toast.info('Aucune memoire non traitee');
      } else {
        toast.success(`${d.created} faits extraits, ${d.reinforced} renforces`);
      }
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur extraction');
    } finally {
      setExtracting(false);
    }
  };

  const handleExtractAll = async () => {
    setExtracting(true);
    try {
      const res = await axios.post(`${API}/persons/${clone.id}/knowledge/extract-all`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = res.data;
      if (d.status === 'no_memories') {
        toast.info('Toutes les memoires sont deja traitees');
      } else {
        toast.success(`Extraction lancee pour ${d.unprocessed_memories} memoires (tache en arriere-plan)`);
      }
      // Poll stats after a delay to show progress
      setTimeout(fetchData, 5000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur extraction');
    } finally {
      setExtracting(false);
    }
  };

  const handleCreate = async () => {
    if (!newItem.content.trim()) return;
    try {
      await axios.post(`${API}/persons/${clone.id}/knowledge`, newItem, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Fait ajoute');
      setAddOpen(false);
      setNewItem({ type: 'FAIT_IMPORTANT', content: '', context: '' });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const handleUpdate = async (itemId, updates) => {
    try {
      await axios.patch(`${API}/persons/${clone.id}/knowledge/${itemId}`, updates, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Mis a jour');
      setEditItem(null);
      fetchData();
    } catch (e) {
      toast.error('Erreur mise a jour');
    }
  };

  const handleDelete = async (itemId) => {
    try {
      await axios.delete(`${API}/persons/${clone.id}/knowledge/${itemId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Supprime');
      fetchData();
    } catch (e) {
      toast.error('Erreur suppression');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="knowledge-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg" data-testid="knowledge-dialog-title">
            <Brain className="w-5 h-5 text-blue-500" />
            Knowledge — {clone?.name || ''}
          </DialogTitle>
          <p className="text-sm text-muted-foreground">Faits structures extraits des interactions</p>
        </DialogHeader>

        {/* Stats cards */}
        {stats && (
          <div className="grid grid-cols-5 gap-2" data-testid="knowledge-stats">
            {[
              { key: 'blocages', label: 'Blocages', val: stats.blocages, c: 'text-red-600 bg-red-500/10' },
              { key: 'promesses', label: 'Promesses', val: stats.promesses, c: 'text-amber-600 bg-amber-500/10' },
              { key: 'faits', label: 'Faits', val: stats.faits, c: 'text-blue-600 bg-blue-500/10' },
              { key: 'decisions', label: 'Decisions', val: stats.decisions, c: 'text-emerald-600 bg-emerald-500/10' },
              { key: 'unprocessed', label: 'Ignorés', val: stats.unprocessed_memories, c: 'text-zinc-500 bg-zinc-500/10' },
            ].map(s => (
              <div key={s.key} className={`rounded-lg p-2 text-center ${s.c}`} data-testid={`stat-${s.key}`}>
                <p className="text-lg font-bold">{s.val}</p>
                <p className="text-[10px]">{s.label}</p>
              </div>
            ))}
          </div>
        )}

        {/* Actions bar */}
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex gap-2">
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="h-8 w-[140px] text-xs" data-testid="filter-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous types</SelectItem>
                <SelectItem value="BLOCAGE">Blocages</SelectItem>
                <SelectItem value="PROMESSE">Promesses</SelectItem>
                <SelectItem value="FAIT_IMPORTANT">Faits</SelectItem>
                <SelectItem value="DECISION">Decisions</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="h-8 w-[120px] text-xs" data-testid="filter-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous statuts</SelectItem>
                <SelectItem value="active">Actifs</SelectItem>
                <SelectItem value="resolved">Resolus</SelectItem>
                <SelectItem value="dismissed">Ignores</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            {canManage && (
              <Button variant="outline" size="sm" className="text-xs gap-1" onClick={() => setAddOpen(!addOpen)} data-testid="add-knowledge-btn">
                <Plus className="w-3.5 h-3.5" /> Ajouter
              </Button>
            )}
            {isAdmin && (
              <>
                <Button variant="outline" size="sm" className="text-xs gap-1" onClick={handleExtract} disabled={extracting || (stats?.unprocessed_memories === 0)} data-testid="extract-knowledge-btn">
                  {extracting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
                  Batch (30)
                </Button>
                <Button size="sm" className="text-xs gap-1" onClick={handleExtractAll} disabled={extracting || (stats?.unprocessed_memories === 0)} data-testid="extract-all-knowledge-btn">
                  {extracting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
                  Tout extraire
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Add form */}
        {addOpen && (
          <div className="border rounded-lg p-3 space-y-2 bg-muted/30" data-testid="add-knowledge-form">
            <div className="flex gap-2">
              <Select value={newItem.type} onValueChange={v => setNewItem(p => ({ ...p, type: v }))}>
                <SelectTrigger className="h-8 w-[160px] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="BLOCAGE">Blocage</SelectItem>
                  <SelectItem value="PROMESSE">Promesse</SelectItem>
                  <SelectItem value="FAIT_IMPORTANT">Fait important</SelectItem>
                  <SelectItem value="DECISION">Decision</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Input
              placeholder="Le fait (ex: Document ANAH manquant)"
              value={newItem.content}
              onChange={e => setNewItem(p => ({ ...p, content: e.target.value }))}
              className="h-8 text-sm"
              data-testid="new-knowledge-content"
            />
            <Input
              placeholder="Contexte (ex: Email a M. Dupont, mars 2026)"
              value={newItem.context}
              onChange={e => setNewItem(p => ({ ...p, context: e.target.value }))}
              className="h-8 text-sm"
              data-testid="new-knowledge-context"
            />
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" size="sm" onClick={() => setAddOpen(false)} className="text-xs">Annuler</Button>
              <Button size="sm" onClick={handleCreate} className="text-xs" data-testid="save-knowledge-btn">Enregistrer</Button>
            </div>
          </div>
        )}

        {/* Items list */}
        <div className="space-y-2 min-h-[100px]" data-testid="knowledge-items-list">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-8 text-sm text-muted-foreground" data-testid="knowledge-empty">
              {stats?.total === 0
                ? 'Aucun fait extrait. Cliquez "Extraire" pour analyser les memoires.'
                : 'Aucun resultat avec ces filtres.'}
            </div>
          ) : (
            items.map(item => (
              <KnowledgeItemCard
                key={item.id}
                item={item}
                canManage={canManage}
                isAdmin={isAdmin}
                editItem={editItem}
                setEditItem={setEditItem}
                onUpdate={handleUpdate}
                onDelete={handleDelete}
              />
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function KnowledgeItemCard({ item, canManage, isAdmin, editItem, setEditItem, onUpdate, onDelete }) {
  const cfg = TYPE_CONFIG[item.type] || TYPE_CONFIG.FAIT_IMPORTANT;
  const statusCfg = STATUS_CONFIG[item.status] || STATUS_CONFIG.active;
  const Icon = cfg.icon;
  const isEditing = editItem === item.id;
  const [editContent, setEditContent] = useState(item.content);
  const [editContext, setEditContext] = useState(item.context || '');

  const conf = Math.round((item.confidence || 0) * 100);
  const date = item.extracted_at ? new Date(item.extracted_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '';

  return (
    <div className={`border rounded-lg p-3 space-y-1.5 ${item.status !== 'active' ? 'opacity-60' : ''}`} data-testid={`knowledge-item-${item.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Icon className={`w-4 h-4 shrink-0 ${cfg.color.includes('red') ? 'text-red-500' : cfg.color.includes('amber') ? 'text-amber-500' : cfg.color.includes('blue') ? 'text-blue-500' : 'text-emerald-500'}`} />
          <Badge variant="outline" className={`text-[10px] shrink-0 ${cfg.color}`}>{cfg.label}</Badge>
          <Badge variant="outline" className={`text-[10px] shrink-0 ${statusCfg.color}`}>{statusCfg.label}</Badge>
          <span className="text-[10px] text-muted-foreground shrink-0">{conf}%</span>
          {item.created_by_type === 'user' && <Badge variant="outline" className="text-[10px] bg-violet-500/10 text-violet-600 border-violet-200">Manuel</Badge>}
        </div>
        <span className="text-[10px] text-muted-foreground shrink-0">{date}</span>
      </div>

      {isEditing ? (
        <div className="space-y-1.5">
          <Input value={editContent} onChange={e => setEditContent(e.target.value)} className="h-7 text-sm" />
          <Input value={editContext} onChange={e => setEditContext(e.target.value)} className="h-7 text-xs" placeholder="Contexte" />
          <div className="flex gap-1 justify-end">
            <Button variant="ghost" size="sm" className="h-6 text-[10px]" onClick={() => setEditItem(null)}>Annuler</Button>
            <Button size="sm" className="h-6 text-[10px]" onClick={() => onUpdate(item.id, { content: editContent, context: editContext })}>Sauver</Button>
          </div>
        </div>
      ) : (
        <>
          <p className="text-sm font-medium pl-6">{item.content}</p>
          {item.context && <p className="text-xs text-muted-foreground pl-6">{item.context}</p>}
          {item.contact_name && <p className="text-[11px] text-muted-foreground pl-6">Contact: {item.contact_name}</p>}
        </>
      )}

      {/* Actions */}
      {canManage && !isEditing && item.status === 'active' && (
        <div className="flex gap-1 pl-6 pt-1">
          <Button variant="ghost" size="sm" className="h-6 text-[10px] gap-1" onClick={() => onUpdate(item.id, { status: 'resolved' })} data-testid={`resolve-${item.id}`}>
            <Check className="w-3 h-3" /> Resoudre
          </Button>
          <Button variant="ghost" size="sm" className="h-6 text-[10px] gap-1" onClick={() => onUpdate(item.id, { status: 'dismissed' })} data-testid={`dismiss-${item.id}`}>
            <Archive className="w-3 h-3" /> Ignorer
          </Button>
          <Button variant="ghost" size="sm" className="h-6 text-[10px] gap-1" onClick={() => { setEditItem(item.id); setEditContent(item.content); setEditContext(item.context || ''); }} data-testid={`edit-${item.id}`}>
            <Pencil className="w-3 h-3" /> Modifier
          </Button>
          {isAdmin && (
            <Button variant="ghost" size="sm" className="h-6 text-[10px] gap-1 text-destructive" onClick={() => onDelete(item.id)} data-testid={`delete-${item.id}`}>
              <Trash2 className="w-3 h-3" /> Supprimer
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
